import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import toastUtils from "../../utils/toastUtils";

import { i18n } from "../../translate/i18n";
import api, { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import moment from "moment";

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState(null);
  
  // Ref para manter referência dos listeners ativos
  const listenersRef = useRef(new Set());

  // REMOVIDO: Interceptors duplicados
  // Os interceptors já estão configurados em services/api.js

  // Effect para inicialização do token
  useEffect(() => {
    const token = localStorage.getItem("token");

    (async () => {
      try {
        if (token) {
          // Configurar o token e verificar se é válido fazendo uma chamada para /auth/me
          api.defaults.headers.Authorization = `Bearer ${JSON.parse(token)}`;

          // Tentar buscar os dados do usuário para validar o token
          const { data } = await api.get("/auth/me");

          // Safe destructuring for user data
          if (data && (data.user || data.id)) {
            setUser(data.user || data);
            setIsAuth(true);
          } else {
            console.warn('❌ Dados de usuário inválidos:', data);
            throw new Error('Invalid user data structure');
          }
        } else {
          setIsAuth(false);
          setUser({});
        }
      } catch (err) {
        console.log("❌ Token inválido no localStorage:", err);
        localStorage.removeItem("token");
        api.defaults.headers.Authorization = undefined;
        setIsAuth(false);
        setUser({});
      } finally {
        // SEMPRE definir loading como false, independente do resultado
        setLoading(false);
      }
    })();
  }, []);

  // Effect para refresh proativo do token antes de expirar
  useEffect(() => {
    if (!isAuth) return;

    // Renovar token a cada 20 horas (antes das 24h de expiração)
    const REFRESH_INTERVAL = 20 * 60 * 60 * 1000; // 20 horas

    const refreshToken = async () => {
      try {
        const { data } = await api.post("/auth/refresh_token");
        if (data?.token) {
          localStorage.setItem("token", JSON.stringify(data.token));
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
        }
      } catch (err) {
        console.warn("⚠️ Falha no refresh proativo do token:", err?.message);
      }
    };

    const intervalId = setInterval(refreshToken, REFRESH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [isAuth]);

  // Effect para configuração do socket
  useEffect(() => {
    if (Object.keys(user).length && user.id > 0) {
      // Verificar se o usuário tem companyId válido
      if (!user.companyId) {
        console.warn("Usuário sem companyId definido:", user);
        return;
      }
      
      // Limpar listeners anteriores
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }

      // Criar nova conexão socket com tratamento de erro melhorado
      try {
        const socketInstance = socketConnection({ user: {
          companyId: user.companyId,
          id: user.id }
        });
      
        if (socketInstance) {
          setSocket(socketInstance);

          // Aguardar um pouco para garantir que o socket está configurado
          setTimeout(() => {
            const eventName = `company-${user.companyId}-user`;

            const handleUserUpdate = (data) => {
              if (data.action === "update" && data.user.id === user.id) {
                setUser(data.user);
              }
            };

            // Verificar se o socket tem o método 'on'
            if (socketInstance && typeof socketInstance.on === 'function') {
              try {
                socketInstance.on(eventName, handleUserUpdate);
                listenersRef.current.add(eventName);
              } catch (error) {
                console.error("Erro ao adicionar listener ao socket:", error);
                setSocket(null);
              }
            } else {
              console.error("Socket instance não tem método 'on'", socketInstance);
              setSocket(null);
            }
          }, 100);
        } else {
          console.warn("Socket instance é null - aplicação funcionará sem socket");
          setSocket(null);
        }
      } catch (socketError) {
        console.error("Erro ao criar socket - possível problema de conectividade:", socketError);
        // Não definir socket em caso de erro, permitindo que a aplicação continue sem socket
        setSocket(null);
      }
    }

    // Cleanup function
    const currentListeners = listenersRef.current;
    return () => {
      if (socket && currentListeners.size > 0) {
        console.log("Limpando listeners do socket para user", user.id);
        currentListeners.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        currentListeners.clear();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, user.companyId]); // Dependências específicas

  // Effect removido - dados do usuário já são buscados na inicialização

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      // IMPORTANTE: Usar openApi para rotas públicas (sem token)
      const { data } = await openApi.post("/auth/login", userData);

      // Safe destructuring for login response
      if (!data || !data.user || !data.token) {
        throw new Error('Invalid login response structure');
      }

      const { user, token } = data;
      const company = user?.company || {};

      // Lógica de configurações da empresa (mantém como estava)
      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        if (setting) {
          // Se "enabled", atendente pode escolher. Se não, assinatura sempre ativa.
          const canToggle = setting.value === "enabled";
          localStorage.setItem("sendSignMessage", canToggle ? "enabled" : "disabled");
          if (!canToggle) {
            // Forçar assinatura ativa quando desabilitado
            localStorage.setItem("persistentSignMessage", true);
          }
        }
      }
      
      localStorage.setItem("profileImage", data.user.profileImage);

      moment.locale("pt-br");
      let dueDate;
      if (data.user.company.id === 1) {
        dueDate = "2999-12-31T00:00:00.000Z";
      } else {
        dueDate = data.user.company.dueDate;
      }
      
      const vencimento = moment(dueDate).format("DD/MM/yyyy");

      var diff = moment(dueDate).diff(moment(moment()).format());
      var before = moment(moment().format()).isBefore(dueDate);
      var dias = moment.duration(diff).asDays();

      if (before === true) {
        localStorage.setItem("token", JSON.stringify(token));
        localStorage.setItem("companyDueDate", vencimento);
        api.defaults.headers.Authorization = `Bearer ${token}`;
        setUser(user);
        setIsAuth(true);
        toastUtils.success(i18n.t("auth.toasts.success"), "Login Realizado");

        if (Math.round(dias) < 5) {
          toastUtils.warning(
            `Sua assinatura vence em ${Math.round(dias)} ${
              Math.round(dias) === 1 ? "dia" : "dias"
            } `,
            "Assinatura Expirando"
          );
        }

        // Redirecionar baseado no perfil do usuário
        if (user && user.profile === "admin") {
          history.push("/");  // Dashboard para admin
        } else {
          history.push("/tickets");  // Tickets para outros usuários
        }
        setLoading(false);
      } else {
        api.defaults.headers.Authorization = `Bearer ${token}`;
        setIsAuth(true);
        toastError(`Opss! Sua assinatura venceu ${vencimento}.
Entre em contato com o Suporte para mais informações! `);
        history.push("/financeiro-aberto");
        setLoading(false);
      }
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      // Limpar socket antes do logout
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
        
        if (typeof socket.disconnect === 'function') {
          socket.disconnect();
        }
      }

      await api.delete("/auth/logout");
      setIsAuth(false);
      setUser({});
      setSocket(null);
      localStorage.removeItem("token");
      localStorage.removeItem("cshow");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      console.log(data);
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    setUser,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    socket,
  };
};

export default useAuth;